import React, { createRef, useEffect, useState } from 'react';
import {
  OrganizationAddressFragment,
  useOrganizationAddressInfoLazyQuery,
  useOrganizationAddressInfoQuery
} from '../graphql';
import { Button, Col, Form, Row, Modal } from 'react-bootstrap';
import { SearchInputWithGql, SearchInputWithGqlDisplay } from './SearchInputWithGql';
import { FIELD_NAMES, formatContactNumber, getConfigurationDataKey } from '../utils';
import { AnyObject } from 'final-form';
import { AddLocationView } from '../views';
import { AnyType } from '../interfaces';
import { useForm } from 'react-final-form';
import { ResourceContent } from '../components';

const { prescriberFields } = FIELD_NAMES;

export const AddSearchLocation = (props: AnyObject) => {
  const form = useForm();
  const ref = createRef<HTMLDivElement>();

  const [locationId, setLocationId] = useState<any>(null);
  const [providerDefaultLocation, setProviderDefaultLocation] = useState<any>(null);
  const organizationId = props?.organizationId || '';
  const { data, loading, refetch } = useOrganizationAddressInfoQuery({ variables: { organizationId } });
  const [locationAdded, setLocationAdded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (organizationId && locationId) {
      refetch();
    }
  }, [locationId]);

  useEffect(() => {
    if (!locationAdded) {
      setLocationId(providerDefaultLocation?.organizationId);
    }
  }, [providerDefaultLocation]);

  useEffect(() => {
    if (!locationAdded) {
      setProviderDefaultLocation(props?.defaultLocation);
    }
  }, [props.defaultLocation, data]);

  useEffect(() => {
    if (locationId && data) {
      props.onLocationSelect(data?.result?.find((p: AnyType) => locationId?.toString() === p?.id));
    }

    if (locationAdded) {
      const location = data?.result?.find((p: AnyType) => locationId?.toString() === p?.id);
      setProviderDefaultLocation(location);
    }
  }, [data, locationId]);

  const toggleAddInitialValue = !!(
    form.getState().values['adding_location'] || form.getState().values['prescriber_location_id']
  );
  const [toggleAdd, setToggleAdd] = useState<boolean>(toggleAddInitialValue);

  const toggleReadFields = () => {
    if (form.getState().values['adding_location'] === false) {
      form.change(prescriberFields.locationId, '');
      form.change(prescriberFields.locationAddress1, '');
      form.change(prescriberFields.locationAddress2, '');
      form.change(prescriberFields.locationPhone2, '');
      form.change(prescriberFields.locationAddressZip, '');
      form.change(prescriberFields.locationAddressCity, '');
      form.change(prescriberFields.locationAddressState, '');
      form.change(prescriberFields.locationPhone1, '');
      form.change(prescriberFields.locationFaxNumber, '');
      form.change(prescriberFields.locationAddressFull, '');
    }
  };

  const onLocationSelect = (location: OrganizationAddressFragment | null) => {
    form.batch(() => {
      form.change('adding_location', false);
      setToggleAdd(false);
      form.change(prescriberFields.locationId, location?.id);
      form.change(prescriberFields.locationAddress1, location?.address1);
      form.change(prescriberFields.locationAddress2, location?.address2);
      form.change(prescriberFields.locationAddressZip, location?.zip);
      form.change(prescriberFields.locationAddressCity, location?.city);
      form.change(prescriberFields.locationAddressState, location?.state);
      form.change(prescriberFields.locationPhone1, formatContactNumber(location?.phone1 ?? location?.phone2));
      form.change(prescriberFields.locationFaxNumber, formatContactNumber(location?.fax));
      form.change(
        prescriberFields.locationAddressFull,
        `${location?.address1} ${location?.address2 ?? ''} | ${location?.city}${
          location?.state || location?.zip ? ', ' : ''
        } ${location?.state ?? ''} ${location?.zip ?? ''}`
      );
    });
    props.onLocationSelect(location);
  };
  const onToggle = () => {
    setToggleAdd((prevToggle: boolean) => {
      form.change('adding_location', !prevToggle);
      form.change(prescriberFields.isNewPrescriber, !prevToggle);
      toggleReadFields();
      if (form.getState().values['adding_location']) {
        setLocationId(null);
      }
      return !prevToggle;
    });
  };
  const renderLabel = (option: OrganizationAddressFragment): string => `${option?.address1}`;

  const getAddedLocation = () => {
    const defaultLocation =
      data?.result?.filter(
        (p: AnyType) => (providerDefaultLocation?.organizationAddressId || providerDefaultLocation?.id) == p?.id
      ) || [];
    if (!props?.providerLocation) {
      props.setProviderLocation(defaultLocation?.[0]);
    }
    return defaultLocation;
  };

  const addedNewLocation = (locationId: number | null | undefined) => {
    setLocationAdded(true);
    setLocationId(locationId);
    setShowModal(false);
  };

  return (
    <>
      <Form.Group {...props.sizes}>
        <p className='nr-text mt-3 mb-2 text-uppercase'>SELECT THE FACILITY LOCATION</p>
        <div className='mb-2'>Search Registered Locations:</div>
        <Row>
          <Col className='mb-3'>
            <Row>
              <Col className='w-75 nr-patient'>
                {locationId &&
                  data?.result &&
                  !loading &&
                  data?.result.filter(
                    (p: AnyType) =>
                      (providerDefaultLocation?.organizationAddressId || providerDefaultLocation?.id) === p?.id
                  ).length > 0 && (
                    <SearchInputWithGql
                      asyncTypeaheadProps={{
                        ...props.asyncTypeaheadProps,
                        ref,
                        defaultSelected: data?.result ? getAddedLocation() : []
                      }}
                      display={SearchInputWithGqlDisplay.outlined}
                      labelRenderer={renderLabel}
                      queryHook={useOrganizationAddressInfoLazyQuery}
                      onItemSelect={onLocationSelect}
                      placeholder='Search Location'
                      isDisabled={props?.isDisabled}
                      npiSearchButtonIcon={props.npiInfoSearch === 'NPISearch' ? 'NPISearch' : ''}
                    />
                  )}
                {!locationId && (
                  <SearchInputWithGql
                    asyncTypeaheadProps={{
                      ref
                    }}
                    display={SearchInputWithGqlDisplay.outlined}
                    labelRenderer={renderLabel}
                    queryHook={useOrganizationAddressInfoLazyQuery}
                    onItemSelect={onLocationSelect}
                    placeholder='Street Name, City, or Zip Code'
                    npiSearchButtonIcon={props.npiInfoSearch === 'NPISearch' ? 'NPISearch' : ''}
                  />
                )}
              </Col>
              <Col>
                <Button
                  variant='outline-secondary-new'
                  className='ml-2 text-nowrap'
                  onClick={() => setShowModal(true)}
                  disabled={props?.isDisabled}>
                  <i className='fa-light fa-location-dot' style={{ marginRight: '8px' }}></i>
                  Add New Location
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form.Group>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size='lg' backdrop='static'>
        <Modal.Header>
          <div className='d-flex w-100 align-items-center justify-content-between'>
            <Modal.Title as='h5'>Add New Location</Modal.Title>
            <p className='required-text general-note mb-0 ms-3'>
              <ResourceContent contentKey={getConfigurationDataKey('general.required-fields-note')} />
            </p>
          </div>
          <button type='button' className='btn-close px-3' onClick={() => setShowModal(false)} />
        </Modal.Header>
        <Modal.Body>
          <AddLocationView
            isStartRequestPage={true}
            patientId={props.patientId}
            addedNewLocation={addedNewLocation}
            prescriberId={props.prescriberId}
            setShowModal={setShowModal}
            setLoading={setIsSubmitting}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};
